import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { MSpinner, MField } from "components/Shared";
import { Button } from "react-md";
import StarRatings from "react-star-ratings";
import { isSatisfactionSurveyAvailable, setSatisfactionSurvey } from "requests/satisfactionSurvey/onboarding";
import responseHandler from "responseHandler";
import { OnboardingSatisfactionSurveyUpdateRequest } from "interfaces/Mueller.Business.Models.ts";
import { DATA_TYPE_DROPDOWN2, DATA_TYPE_MULTI_LINE_STRING, DATA_TYPE_CHECKBOX, DATA_TYPE_PHONE_NUMBER } from "typeConstants/dataTypes";

interface Props {
  match: { params: { uId: string } },
  makeToast: (arg: string) => void,
}

let STATUS = {
  NONE: "NONE",
  FETCHING: "FETCHING",
  READY: "READY",
  RATED: "RATED",
  EXPIRED: "EXPIRED",
  ERROR: "ERROR",
};

const OnboardingSatisfactionSurvey = (props: Props) => {
  const mounted = useRef(true);
  const { match: { params }, makeToast } = props;
  const [status, setStatus] = useState(STATUS.NONE);

  const starRatedColorConst = "#E03D4C";
  const starHoverColorConst = "#CA2030";

  const [surveyResponse, setSurveyResponse] = useState<OnboardingSatisfactionSurveyUpdateRequest>({
    uid: params.uId,
    onboardingExperienceScore: undefined,
    whatWasMostHelpfulDuringOnboarding: undefined,
    whatSingleChangeWouldImproveOnboarding: undefined,
    didYouFeelPreparedForYourFirstInspection: undefined,
    whichPartOfOnboardingShouldBeImproved: undefined,
    explainWhichPartOfOnboardingShouldBeImproved: undefined,
    doYouUnderstandJobResponsibilitiesAndHowPerformanceIsMeasured: undefined,
    unclearOnPerformance: undefined,
    unclearOnResponsibilities: undefined,
    whatResponsibilitiesOrPerformanceExpectationsCouldBeClarified: undefined,
    requestedCallForProcessImprovement: undefined,
    contactNumber: undefined,
  });
  const [unclearOther, setUnclearOther] = useState<boolean>(false);

  useEffect(() => {
    if (status === STATUS.NONE)
      fetchStatus();

    return () => { mounted.current = false; };
  }, []);

  const fetchStatus = () => {
    isSatisfactionSurveyAvailable(
      params.uId,
      (data) => { data && data.canCompleteSatisfactionSurvey ? setStatus(STATUS.READY) : setStatus(STATUS.EXPIRED); },
      () => { setStatus(STATUS.ERROR); },
      mounted
    );
    setStatus(STATUS.FETCHING);
  };

  const updateSurveyResponse = (key: string, val: any) => {
    setSurveyResponse((prevSurveyResponse) => ({
      ...prevSurveyResponse,
      [key]: val
    }));
  }

  const submitFeedback = () => {
    let passesValidation = null;
    let responseToSubmit = { ...surveyResponse };
    responseToSubmit.uid = params.uId;
    Object.keys(responseToSubmit).forEach((key) => {
      const element = document.getElementById(key);
      if (responseToSubmit[key] === null) {
        passesValidation = key;
        if (element) {
          element.classList.add("error-highlight"); // Add red text class
        }
      } else {
        if (element) {
          element.classList.remove("error-highlight"); // Remove red text class
        }
      }
    });

    if (passesValidation !== null) {
      const element = document.getElementById(passesValidation);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      makeToast("Please fill out each question before submitting.");
      return;
    }

    setSatisfactionSurvey(params.uId, responseToSubmit, () => {
      setStatus(STATUS.RATED);
    }, () => setStatus(STATUS.READY), mounted);
    setStatus(STATUS.FETCHING);
  };

  return (
    <div className="satisfaction-survey-root">
      {(status === STATUS.NONE || status === STATUS.FETCHING) && <MSpinner />}
      {status === STATUS.READY &&
        <>
          <h3>Your feedback is important to us!</h3>
          <div className="rating-tip">
            (1 - Poor, 2 - Needs Improvement, 3 - Average, 4 - Good, 5 - Excellent)
          </div>
          <h4 id="onboardingExperienceScore">
            How would you rate the onboarding process at Mueller Reports?
          </h4>
          <StarRatings
            rating={surveyResponse.onboardingExperienceScore ?? 0}
            changeRating={(nextRating) => updateSurveyResponse("onboardingExperienceScore", nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst}
          />
          {surveyResponse.onboardingExperienceScore === 5 && (
            <>
              <h6 id="whatWasMostHelpfulDuringOnboarding">
                What was the most helpful part of your onboarding?
              </h6>
              <MField
                key="whatWasMostHelpfulDuringOnboarding"
                dataType={DATA_TYPE_MULTI_LINE_STRING}
                onChange={(e, i) => updateSurveyResponse("whatWasMostHelpfulDuringOnboarding", i)}
              />
            </>
          )}
          {surveyResponse.onboardingExperienceScore !== undefined &&
            surveyResponse.onboardingExperienceScore < 5 && (
              <>
                <h6 id="whatSingleChangeWouldImproveOnboarding">
                  What single change would most improve onboarding at Mueller?
                </h6>
                <MField
                  key="whatSingleChangeWouldImproveOnboarding"
                  dataType={DATA_TYPE_MULTI_LINE_STRING}
                  onChange={(e, i) => updateSurveyResponse("whatSingleChangeWouldImproveOnboarding", i)}
                />
              </>
            )}

          <h4 id="didYouFeelPreparedForYourFirstInspection">
            Did you feel prepared for your first inspection?
          </h4>
          <MField
            key="didYouFeelPreparedForYourFirstInspection"
            dataType={DATA_TYPE_DROPDOWN2}
            dataTypeOptions={{
              options: [
                { label: "Yes", value: true },
                { label: "No", value: false },
              ],
            }}
            onChange={(e, i) => updateSurveyResponse("didYouFeelPreparedForYourFirstInspection", i?.value)}
          />
          {surveyResponse.didYouFeelPreparedForYourFirstInspection === false && (
            <>
              <h6 id="whichPartOfOnboardingShouldBeImproved">
                Which part of our onboarding process needs to be improved? (Select all that apply)
              </h6>
              <div className="survey-checkbox-col">
                {[
                  "Job Advertisement",
                  "VIITA course",
                  "Interview Process",
                  "Video Modules",
                  "Logistics Manager Support",
                  "Other"
                ].map(option => (
                  <label key={option} className="survey-checkbox-label">
                    <input
                      type="checkbox"
                      className="survey-checkbox-input"
                      checked={
                        (surveyResponse.whichPartOfOnboardingShouldBeImproved || "")
                          .split(",")
                          .map(s => s.trim())
                          .includes(option)
                      }
                      onChange={e => {
                        const selected = (surveyResponse.whichPartOfOnboardingShouldBeImproved || "")
                          .split(",")
                          .map(s => s.trim())
                          .filter(Boolean);
                        let next: string[];
                        if (e.target.checked) {
                          next = [...selected, option];
                        } else {
                          next = selected.filter(val => val !== option);
                        }
                        updateSurveyResponse("whichPartOfOnboardingShouldBeImproved", next.join(", "));
                      }}
                    />
                    {option}
                  </label>
                ))}
              </div>
              <h6 id="explainWhichPartOfOnboardingShouldBeImproved">
                Please explain where we can improve our process based on your selection(s) above.
              </h6>
              <MField
                key="explainWhichPartOfOnboardingShouldBeImproved"
                dataType={DATA_TYPE_MULTI_LINE_STRING}
                onChange={(e, i) => updateSurveyResponse("explainWhichPartOfOnboardingShouldBeImproved", i)}
              />
            </>
          )}

          <h4 id="doYouUnderstandJobResponsibilitiesAndHowPerformanceIsMeasured">
            Do you clearly understand your responsibilities and how performance is measured?
          </h4>
          <MField
            key="doYouUnderstandJobResponsibilitiesAndHowPerformanceIsMeasured"
            dataType={DATA_TYPE_DROPDOWN2}
            dataTypeOptions={{
              options: [
                { label: "Yes", value: true },
                { label: "No", value: false },
              ],
            }}
            onChange={(e, i) => updateSurveyResponse("doYouUnderstandJobResponsibilitiesAndHowPerformanceIsMeasured", i?.value)}
          />
          {surveyResponse.doYouUnderstandJobResponsibilitiesAndHowPerformanceIsMeasured === false && (
            <>
              <h6 id="unclearOnPerformanceResponsibilities">
                What was unclear? (Select all that apply)
              </h6>
              <div className="survey-checkbox-col">
                {[
                  { label: "I was unclear on how my performance was measured", value: "performance" },
                  { label: "I was unclear on the position responsibilities", value: "responsibilities" },
                  { label: "Other", value: "other" }
                ].map(option => (
                  <label key={option.value} className="survey-checkbox-label">
                    <input
                      type="checkbox"
                      className="survey-checkbox-input"
                      checked={
                        (option.value === "performance" && surveyResponse.unclearOnPerformance) ||
                        (option.value === "responsibilities" && surveyResponse.unclearOnResponsibilities) ||
                        (option.value === "other" && unclearOther)
                      }
                      onChange={e => {
                        if (option.value === "performance") {
                          updateSurveyResponse("unclearOnPerformance", e.target.checked);
                        }
                        if (option.value === "responsibilities") {
                          updateSurveyResponse("unclearOnResponsibilities", e.target.checked);
                        }
                        if (option.value === "other") {
                          setUnclearOther(e.target.checked);
                        }
                      }}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
              {(unclearOther) && (
                <>
                  <h6 id="whatResponsibilitiesOrPerformanceExpectationsCouldBeClarified">
                    Please explain what is unclear about performance/position responsibilities.
                  </h6>
                  <MField
                    key="whatResponsibilitiesOrPerformanceExpectationsCouldBeClarified"
                    dataType={DATA_TYPE_MULTI_LINE_STRING}
                    onChange={(e, i) => updateSurveyResponse("whatResponsibilitiesOrPerformanceExpectationsCouldBeClarified", i)}
                  />
                </>
              )}
            </>
          )}

          <h4 id="requestedCallForProcessImprovement">
            Would you like to receive a call from our process improvement team to discuss your feedback above?
          </h4>
          <MField
            key="requestedCallForProcessImprovement"
            dataType={DATA_TYPE_DROPDOWN2}
            dataTypeOptions={{
              options: [
                { label: "Yes", value: true },
                { label: "No", value: false },
              ],
            }}
            onChange={(e, i) => updateSurveyResponse("requestedCallForProcessImprovement", i?.value)}
          />
          {surveyResponse.requestedCallForProcessImprovement === true && (
            <>
              <h6 id="contactNumber">
                Please provide a contact number where you can be reached:
              </h6>
              <MField
                key="contactNumber"
                dataType={DATA_TYPE_PHONE_NUMBER}
                onChange={(e, i) => updateSurveyResponse("contactNumber", i)}
              />
            </>
          )}
          <h3>Thank you for your time!</h3>
          <Button primary raised onClick={submitFeedback}>Submit</Button>
        </>
      }
      {status === STATUS.RATED &&
        <div className="satisfaction-survey-complete">
          <h3>Thank you for your feedback!</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button primary raised>Contact Us</Button>
          </a>
        </div>
      }
      {status === STATUS.EXPIRED &&
        <div className="satisfaction-survey-complete">
          <h3>This link has expired.  Please contact customer service with any additional feedback.</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button primary raised>Contact Us</Button>
          </a>
        </div>
      }
      {status === STATUS.ERROR &&
        <div className="satisfaction-survey-complete">
          <h3>Oops!  You appear to have reached this page in error.</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button primary raised>Contact Us</Button>
          </a>
        </div>
      }
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  makeToast: text => {
    responseHandler.customToast(text, "error", dispatch);
  }
});

export default connect(null, mapDispatchToProps)(OnboardingSatisfactionSurvey);